import { useEffect, useState, useCallback, useRef } from "react";
import { validateRodCislo, validateRodCisloForChild } from "../utils/rodCisloValidation";
import {
    IconLoader,
    IconCheck,
    IconUser,
    IconDeviceFloppy,
    IconSend,
    IconCalendar,
    IconCreditCard,
    IconX,
    IconInfoCircle,
    IconDownload,
    IconSchool
} from "@tabler/icons-react";
import { authenticatedFetch } from '../utils/auth';
import {
    calculatePriceFallback,
    fetchPriceBreakdown
} from '../utils/priceCalculation';
import PersonForm from './PersonForm';

import { useNavigate } from "react-router-dom";

interface Person {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    address: string;
    houseNumber?: string;
    city?: string;
    psc?: string;
    rodCislo?: string;
    isBernolakova: boolean;
    schoolName?: string;
    gender?: 'MALE' | 'FEMALE';
    type: 'PARENT' | 'ADULT' | 'KID';
    schoolyearId?: string;
    schoolyear?: {
        id: string;
        name: string;
        order: number;
        type: 'PRESCHOOL' | 'PRIMARY' | 'HIGHSCHOOL' | 'UNIVERSITY';
    };
}

interface Kruzok {
    id: string;
    name: string;
    description?: string;
    price: number;
    capacity: number;
    currentRegistrations: number;
    time: string;
    surcharge: boolean;
    isStretko: boolean;
    gender: 'MALE' | 'FEMALE' | 'BOTH';
    type: 'TRIH' | 'BERN';
    requiresZapisne: boolean;
    schoolYear?: Array<{
        id: string;
        name: string;
        order: number;
        type: 'PRESCHOOL' | 'PRIMARY' | 'HIGHSCHOOL' | 'UNIVERSITY';
    }>;
}

interface SchoolYear {
    id: string;
    name: string;
    order: number;
    type: 'PRESCHOOL' | 'PRIMARY' | 'HIGHSCHOOL' | 'UNIVERSITY';
}

interface Settings {
    id: string;
    VPvalue?: number;
    CPvalue?: number;
    CPdate?: string;
    finalDate?: string;
    zapisneValue?: number;
    baseKruzok?: number;
    baseStretko?: number;
    VPvalueSsAnim?: number;
    CPvalueSsAnim?: number;
    zapisneValueSsAnim?: number;
    baseKruzokSsAnim?: number;
    baseStretkoSsAnim?: number;
    bernKazdyDalsi?: number;
    principal?: string;
    updatedAt: string;
}

interface SharedPrihlaskaFormProps {
    person: Person;
    existingPrihlaska?: any;
    isAdminMode?: boolean;
    onSuccess?: (prihlaaskaId: string) => void;
    onCancel?: () => void;
}

const SharedPrihlaskaForm = ({
    person: initialPerson,
    existingPrihlaska,
    isAdminMode = false,
    onSuccess,
    onCancel
}: SharedPrihlaskaFormProps) => {
    const [person, setPerson] = useState<Person>(initialPerson);
    const [kruzky, setKruzky] = useState<Kruzok[]>([]);
    const [filteredKruzky, setFilteredKruzky] = useState<Kruzok[]>([]);
    const [userCompatibleKruzky, setUserCompatibleKruzky] = useState<string[]>([]); // IDs of kruzky visible to users
    const [selectedKruzky, setSelectedKruzky] = useState<string[]>([]);
    const [schoolyears, setSchoolyears] = useState<SchoolYear[]>([]);
    const [settings, setSettings] = useState<Settings | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submittingStatus, setSubmittingStatus] = useState<'DRAFT' | 'GENERATED' | 'ACCEPTED' | null>(null);
    const [savingDraft, setSavingDraft] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [draftSaved, setDraftSaved] = useState(false);
    const [existingDraftId, setExistingDraftId] = useState<string | null>(null);
    const [hasExistingPrihlaska, setHasExistingPrihlaska] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [isPersonReadOnly, setIsPersonReadOnly] = useState(false);
    const [generatedPrihlaskaId, setGeneratedPrihlaskaId] = useState<string | null>(null);

    // Auto-save related state
    const [autoSaving, setAutoSaving] = useState(false);
    const [lastAutoSaveTime, setLastAutoSaveTime] = useState<Date | null>(null);
    const [isAutoSaveUpdate, setIsAutoSaveUpdate] = useState(false);
    const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const navigate = useNavigate();
    // Isanim state for checking animators
    const [isAnimList, setIsAnimList] = useState<Array<{ id: string; rodCislo: string; name: string }>>([]);

    // Price breakdown state
    const [priceBreakdown, setPriceBreakdown] = useState<any>(null);
    const [totalPrice, setTotalPrice] = useState<number>(0);

    // Date validation state
    const [dateStatus, setDateStatus] = useState<{
        canCreate: boolean;
        canEdit: boolean;
        canSubmit: boolean;
        canSelectDiscounts: boolean;
        message: string | null;
        startDate: string | null;
        finalDate: string | null;
    } | null>(null);

    // VP/CP and payment options
    const [selectedVP, setVzdelavacyPoukaz] = useState<boolean>(false);
    const [selectedCP, setCestnePrehlasenie] = useState<boolean>(false);
    const [now, setNow] = useState(Date.now);
    const discountsAllowed = isAdminMode || Boolean(existingPrihlaska && existingPrihlaska.status !== 'DRAFT') ||
        (dateStatus?.canSelectDiscounts !== false &&
            (!settings?.finalDate || now <= new Date(settings.finalDate).getTime()));
    const vzdelavacyPoukaz = discountsAllowed && selectedVP;
    const cestnePrehlasenie = discountsAllowed && selectedCP;
    const [paymentOption, setPaymentOption] = useState<'CASH' | 'EDUPAGE' | null>(null);

    // Edit person state
    const [editFormData, setEditFormData] = useState<Partial<Person>>({});
    const suppressClearRef = useRef(false);

    useEffect(() => {
        if (isAdminMode || !settings?.finalDate) return;
        const deadline = new Date(settings.finalDate).getTime();
        if (now > deadline) return;

        const timeout = setTimeout(
            () => setNow(Date.now()),
            Math.min(Math.max(0, deadline - Date.now() + 1), 2147483647)
        );
        return () => clearTimeout(timeout);
    }, [isAdminMode, settings?.finalDate, now]);

    // Helper function to use appropriate fetch method based on admin mode
    const fetchWithAuth = useCallback((url: string, options: RequestInit = {}) => {
        if (isAdminMode) {
            return authenticatedFetch(url, options);
        } else {
            return fetch(url, {
                ...options,
                credentials: 'include'
            });
        }
    }, [isAdminMode]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Also update the person state to reflect the current person data
                setPerson(person);

                // Fetch available schoolyears
                const schoolyearRes = await fetchWithAuth(`${process.env.REACT_APP_PATH}/api/vseob/rocnik`, {});

                if (schoolyearRes.ok) {
                    const schoolyearData = await schoolyearRes.json();
                    setSchoolyears(schoolyearData.data || []);
                }

                // Handle existing prihlaska data
                if (existingPrihlaska) {
                    if (existingPrihlaska.status === 'DRAFT') {
                        // Load existing draft data - person data can still be edited
                        setExistingDraftId(existingPrihlaska.id);
                        setSelectedKruzky(existingPrihlaska.kruzky?.map((k: any) => k.id) || []);
                        setVzdelavacyPoukaz(existingPrihlaska.isVP || false);
                        setCestnePrehlasenie(existingPrihlaska.isCp || false);
                        setPaymentOption(existingPrihlaska.paymentOption || null);
                        setIsPersonReadOnly(false);
                    } else if (existingPrihlaska.status === 'GENERATED') {
                        // Generated prihlaska - show in read-only mode with revoke option
                        setHasExistingPrihlaska(true);
                        setSelectedKruzky(existingPrihlaska.kruzky?.map((k: any) => k.id) || []);
                        setVzdelavacyPoukaz(existingPrihlaska.isVP || false);
                        setCestnePrehlasenie(existingPrihlaska.isCp || false);
                        setPaymentOption(existingPrihlaska.paymentOption || null);
                        setIsPersonReadOnly(!isAdminMode); // Admin can edit even generated prihlasky
                    } else {
                        // Other statuses (ACCEPTED, REJECTED, etc.) - fully read-only unless admin
                        setHasExistingPrihlaska(true);
                        setSelectedKruzky(existingPrihlaska.kruzky?.map((k: any) => k.id) || []);
                        setVzdelavacyPoukaz(existingPrihlaska.isVP || false);
                        setCestnePrehlasenie(existingPrihlaska.isCp || false);
                        setPaymentOption(existingPrihlaska.paymentOption || null);
                        setIsPersonReadOnly(!isAdminMode); // Admin can edit any status
                    }
                }

                // Fetch available kruzky
                const kruzkyRes = await fetchWithAuth(`${process.env.REACT_APP_PATH}/api/kruzok`, {});

                if (kruzkyRes.ok) {
                    const kruzkyData = await kruzkyRes.json();
                    const allKruzky = kruzkyData.data || kruzkyData;
                    setKruzky(allKruzky);

                    // If user opens an existing draft/concept, auto-unselect any selected kruzky that are now full
                    try {
                        if (!isAdminMode && selectedKruzky && selectedKruzky.length > 0) {
                            const fullSelected = selectedKruzky
                                .map(id => allKruzky.find((k: any) => k.id === id))
                                .filter((k: any) => k && k.capacity !== -1 && (k.currentRegistrations ?? 0) >= k.capacity);

                            if (fullSelected.length > 0) {
                                const fullNames = fullSelected.map((k: any) => k.name).join(', ');
                                const newSelected = selectedKruzky.filter((id: string) => !fullSelected.some((k: any) => k.id === id));
                                // Prevent the "clear messages" effect from wiping this informative message
                                suppressClearRef.current = true;
                                setSelectedKruzky(newSelected);
                                setErrorAndScroll(`Nasledujúce krúžky boli automaticky odhlásené, pretože sú plné: ${fullNames}`);
                            }
                        }
                    } catch (e) {
                        console.error('Error auto-unselecting full kruzky on open:', e);
                    }

                    // Apply filtering based on gender and schoolyear compatibility
                    const compatibleKruzky = filterCompatibleKruzky(allKruzky, person);

                    if (isAdminMode) {
                        // In admin mode, show all kruzky but track which ones are user-compatible
                        setFilteredKruzky(allKruzky);
                        setUserCompatibleKruzky(compatibleKruzky.map(k => k.id));
                    } else {
                        // In user mode, only show compatible kruzky
                        setFilteredKruzky(compatibleKruzky);
                    }
                }

                // Fetch settings
                const settingsRes = await fetchWithAuth(`${process.env.REACT_APP_PATH}/api/vseob/settings`, {});

                if (settingsRes.ok) {
                    const settingsData = await settingsRes.json();
                    setSettings(settingsData.data);
                }

                // Fetch isanim list for price calculation
                const isAnimRes = await fetchWithAuth(`${process.env.REACT_APP_PATH}/api/vseob/isanim`, {});

                if (isAnimRes.ok) {
                    const isAnimData = await isAnimRes.json();
                    setIsAnimList(isAnimData.data || []);
                }

                // Fetch date validation status (only for user mode)
                if (!isAdminMode) {
                    const dateStatusRes = await fetchWithAuth(`${process.env.REACT_APP_PATH}/api/prihlaska/date-status`, {});

                    if (dateStatusRes.ok) {
                        const dateStatusData = await dateStatusRes.json();
                        setDateStatus(dateStatusData.data);
                    }
                }

                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch data:', err);
                setError('Nepodarilo sa načítať údaje');
                setLoading(false);
            }
        };

        fetchData();
        // We intentionally omit `selectedKruzky` from deps to run this logic only when the form/person/prihlaska opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [person, existingPrihlaska, isAdminMode, fetchWithAuth]);

    // Initialize form data once when component mounts or person changes
    useEffect(() => {
        const newEditFormData = {
            firstName: initialPerson.firstName || '',
            lastName: initialPerson.lastName || '',
            email: initialPerson.email || '',
            phone: initialPerson.phone || '',
            address: initialPerson.address || '',
            houseNumber: initialPerson.houseNumber || '',
            city: initialPerson.city || '',
            psc: initialPerson.psc || '',
            rodCislo: initialPerson.rodCislo || '',
            isBernolakova: initialPerson.isBernolakova || false,
            schoolName: initialPerson.schoolName || '',
            gender: initialPerson.gender || 'MALE',
            schoolyearId: initialPerson.schoolyearId || initialPerson.schoolyear?.id || '',
            type: initialPerson.type || 'KID'
        };

        setEditFormData(newEditFormData);
        setPerson(initialPerson);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialPerson.id]); // Only re-run when person ID changes

    // Re-filter kruzky when person data or edit form data changes
    useEffect(() => {
        if (person && kruzky.length > 0) {
            // Use edit form data if available, otherwise use saved person data
            const currentPersonData = {
                ...person,
                gender: editFormData.gender || person.gender,
                schoolyearId: editFormData.schoolyearId || person.schoolyearId,
                isBernolakova: editFormData.isBernolakova !== undefined ? editFormData.isBernolakova : person.isBernolakova
            };
            const compatibleKruzky = filterCompatibleKruzky(kruzky, currentPersonData);

            if (isAdminMode) {
                // For admin mode, show all kruzky but track which ones are user-compatible
                setFilteredKruzky(kruzky);
                setUserCompatibleKruzky(compatibleKruzky.map(k => k.id));
            } else {
                setFilteredKruzky(compatibleKruzky);
            }
        }
    }, [person, kruzky, editFormData.gender, editFormData.schoolyearId, editFormData.isBernolakova, isAdminMode]);

    // Deselect incompatible kruzky when edit form data changes
    useEffect(() => {
        if (person && kruzky.length > 0 && selectedKruzky.length > 0 && !isAdminMode) { // Skip for admin mode
            const currentPersonData = {
                ...person,
                gender: editFormData.gender || person.gender,
                schoolyearId: editFormData.schoolyearId || person.schoolyearId,
                isBernolakova: editFormData.isBernolakova !== undefined ? editFormData.isBernolakova : person.isBernolakova
            };
            const compatibleKruzky = filterCompatibleKruzky(kruzky, currentPersonData);
            const compatibleKruzkyIds = compatibleKruzky.map(k => k.id);

            setSelectedKruzky(prevSelected =>
                prevSelected.filter(kruzokId => compatibleKruzkyIds.includes(kruzokId))
            );
        }
    }, [editFormData.gender, editFormData.schoolyearId, editFormData.isBernolakova, person, kruzky, selectedKruzky.length, isAdminMode]);

    // Clear messages when user makes changes
    useEffect(() => {
        // If a change intentionally set selectedKruzky and we wanted to keep the message, skip clearing once
        if (suppressClearRef.current) {
            suppressClearRef.current = false;
            return;
        }

        setDraftSaved(false);
        setError(null);
    }, [selectedKruzky, vzdelavacyPoukaz, cestnePrehlasenie, paymentOption, editFormData]);

    // Auto-save functionality - debounced save after user stops typing
    const autoSaveChanges = useCallback(async (formData: Partial<Person>) => {
        if (!person || (isPersonReadOnly && !isAdminMode)) return;

        setAutoSaving(true);

        try {
            // Check if there are changes to save
            const hasPersonChanges = Object.keys(formData).some(key => {
                const formValue = formData[key as keyof Person];
                const personValue = person[key as keyof Person];
                return formValue !== personValue && formValue !== '';
            });

            if (hasPersonChanges) {
                const endpoint = isAdminMode
                    ? `${process.env.REACT_APP_PATH}/api/admin/person/${person.id}`
                    : `${process.env.REACT_APP_PATH}/api/user/person/${person.id}`;

                const personRes = await fetchWithAuth(endpoint, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(formData),
                });

                if (!personRes.ok) {
                    console.warn('Auto-save failed for person data');
                    return;
                }

                const result = await personRes.json();
                setPerson(result.person || result.data);

                // Update edit form data to reflect saved state (without triggering auto-save)
                setIsAutoSaveUpdate(true);
                const updatedPerson = result.person || result.data;
                setEditFormData({
                    firstName: updatedPerson.firstName,
                    lastName: updatedPerson.lastName,
                    email: updatedPerson.email || '',
                    phone: updatedPerson.phone || '',
                    address: updatedPerson.address,
                    houseNumber: updatedPerson.houseNumber || '',
                    city: updatedPerson.city || '',
                    psc: updatedPerson.psc || '',
                    rodCislo: updatedPerson.rodCislo || '',
                    isBernolakova: updatedPerson.isBernolakova,
                    schoolName: updatedPerson.schoolName || '',
                    gender: updatedPerson.gender || 'MALE',
                    schoolyearId: updatedPerson.schoolyearId || '',
                    type: updatedPerson.type || 'KID'
                });

                // Reset the flag after a short delay
                setTimeout(() => setIsAutoSaveUpdate(false), 100);

                setLastAutoSaveTime(new Date());
            }
        } catch (err) {
            console.warn('Auto-save failed:', err);
        } finally {
            setAutoSaving(false);
        }
    }, [person, isAdminMode, isPersonReadOnly, fetchWithAuth]);

    // Debounced auto-save effect - triggers 2 seconds after user stops typing
    useEffect(() => {
        // Don't trigger auto-save if this is an update from auto-save itself
        if (isAutoSaveUpdate) return;

        // Clear existing timeout
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }

        // Set new timeout for auto-save
        autoSaveTimeoutRef.current = setTimeout(() => {
            autoSaveChanges(editFormData);
        }, 2000); // 2 second delay

        // Cleanup function
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editFormData, isAutoSaveUpdate]); // Depend on the flag too

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, []);

    // Helper function to determine if person should automatically get VP discount
    const shouldAutoApplyVP = useCallback((): boolean => {
        if (!person) return false;

        // University students automatically get VP discount
        const currentSchoolyearId = editFormData.schoolyearId || person.schoolyearId;

        if (currentSchoolyearId) {
            const schoolyear = schoolyears.find(sy => sy.id === currentSchoolyearId);
            return schoolyear?.type === 'UNIVERSITY';
        }

        return false;
    }, [person, editFormData.schoolyearId, schoolyears]);

    // Update price breakdown when relevant data changes
    useEffect(() => {
        if (person && selectedKruzky.length > 0) {
            const updatePriceBreakdown = async () => {
                // Include automatic VP for university students
                const autoApplyVP = shouldAutoApplyVP();
                const effectiveVP = vzdelavacyPoukaz || autoApplyVP;

                const breakdown = await fetchPriceBreakdown(
                    person.id,
                    selectedKruzky,
                    effectiveVP,
                    cestnePrehlasenie,
                    existingPrihlaska?.id,
                    isAdminMode
                );
                if (breakdown) {
                    setPriceBreakdown(breakdown);
                    setTotalPrice(breakdown.totalPrice);
                }
            };
            updatePriceBreakdown();
        } else {
            setPriceBreakdown(null);
            setTotalPrice(0);
        }
    }, [selectedKruzky, vzdelavacyPoukaz, cestnePrehlasenie, person, existingPrihlaska?.id, shouldAutoApplyVP, isAdminMode]);

    // Helper function to check if a kruzok is full
    const isKruzokFull = (kruzok: Kruzok): boolean => {
        // If capacity is -1, it means unlimited capacity
        if (kruzok.capacity === -1) return false;
        return kruzok.currentRegistrations >= kruzok.capacity;
    };

    const handleKruzokToggle = (kruzokId: string) => {
        // Don't allow toggling if outside date range (only in user mode)
        if (!isAdminMode && dateStatus && !dateStatus.canEdit) {
            return;
        }

        // Find the kruzok to check if it's full
        const kruzok = kruzky.find(k => k.id === kruzokId);
        if (!kruzok) return;

        // If trying to select a full kruzok, don't allow it (unless admin mode)
        if (!isAdminMode && !selectedKruzky.includes(kruzokId) && isKruzokFull(kruzok)) {
            return;
        }

        setSelectedKruzky(prev =>
            prev.includes(kruzokId)
                ? prev.filter(id => id !== kruzokId)
                : [...prev, kruzokId]
        );
    };

    const handleSaveDraft = async () => {
        if (!person) return;

        setSavingDraft(true);
        setError(null);

        try {
            // Check if there are changes to save
            const hasPersonChanges = Object.keys(editFormData).some(key => {
                const formValue = editFormData[key as keyof Person];
                const personValue = person[key as keyof Person];
                return formValue !== personValue && formValue !== '';
            });

            let updatedPersonId = person.id;

            if (hasPersonChanges) {
                const endpoint = isAdminMode
                    ? `${process.env.REACT_APP_PATH}/api/admin/person/${person.id}`
                    : `${process.env.REACT_APP_PATH}/api/user/person/${person.id}`;

                const personRes = await fetchWithAuth(endpoint, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(editFormData),
                });

                if (!personRes.ok) {
                    const errorData = await personRes.json();
                    throw new Error(errorData.error || 'Nepodarilo sa aktualizovať údaje osoby');
                }

                const result = await personRes.json();
                setPerson(result.person);
                updatedPersonId = result.person.id;

                // Update edit form data to reflect saved state
                setEditFormData({
                    firstName: result.person.firstName,
                    lastName: result.person.lastName,
                    email: result.person.email || '',
                    phone: result.person.phone || '',
                    address: result.person.address,
                    houseNumber: result.person.houseNumber || '',
                    city: result.person.city || '',
                    psc: result.person.psc || '',
                    rodCislo: result.person.rodCislo || '',
                    isBernolakova: result.person.isBernolakova,
                    schoolName: result.person.schoolName || '',
                    gender: result.person.gender || 'MALE',
                    schoolyearId: result.person.schoolyearId || '',
                    type: result.person.type || 'KID'
                });
            }

            // Save draft prihlaska data (allow saving even with no selections)
            const autoApplyVP = shouldAutoApplyVP();
            const effectiveVP = vzdelavacyPoukaz || autoApplyVP;

            const draftData = {
                personId: updatedPersonId,
                kruzkyIds: selectedKruzky,
                vzdelavacyPoukaz: effectiveVP,
                cestnePrehlasenie: cestnePrehlasenie,
                paymentOption: paymentOption,
                toPay: calculateTotalPrice()
            };

            const endpoint = isAdminMode
                ? `${process.env.REACT_APP_PATH}/api/admin/prihlaska${existingDraftId ? `/${existingDraftId}` : ''}`
                : `${process.env.REACT_APP_PATH}/api/prihlaska${existingDraftId ? `/${existingDraftId}` : ''}`;

            if (existingDraftId) {
                // Update existing draft
                const updateRes = await fetch(endpoint, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify(draftData),
                });

                if (!updateRes.ok) {
                    const errorData = await updateRes.json();
                    throw new Error(errorData.error || 'Nepodarilo sa aktualizovať návrh');
                }
            } else {
                // Create new draft
                const createRes = await fetchWithAuth(endpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(draftData),
                });

                if (!createRes.ok) {
                    const errorData = await createRes.json();
                    throw new Error(errorData.error || 'Nepodarilo sa vytvoriť návrh');
                }

                const result = await createRes.json();
                setExistingDraftId(result.data.id);
            }

            // Show success message briefly
            setError(null);
            setDraftSaved(true);
            setTimeout(() => setDraftSaved(false), 3000); // Hide success message after 3 seconds

        } catch (err) {
            console.error('Failed to save draft:', err);
            setError(err instanceof Error ? err.message : 'Nepodarilo sa uložiť návrh');
        }

        setSavingDraft(false);
    };

    // Helper function to set error and scroll to bottom for better visibility
    const setErrorAndScroll = (errorMessage: string) => {
        setError(errorMessage);
        // Small delay to ensure error message is rendered before scrolling
        setTimeout(() => {
            window.scrollTo({
                top: document.body.scrollHeight,
                behavior: 'smooth'
            });
        }, 100);
    };

    // Re-fetch latest kruzky and verify selected ones still have capacity.
    const ensureKruzokCapacityBeforeSubmit = async (): Promise<boolean> => {
        // Skip capacity checks for admin mode
        if (isAdminMode) {
            return true;
        }

        try {
            const kruzkyRes = await fetchWithAuth(`${process.env.REACT_APP_PATH}/api/kruzok`, {});
            if (!kruzkyRes.ok) {
                setErrorAndScroll('Nepodarilo sa overiť kapacity krúžkov. Skúste to prosím neskôr.');
                return false;
            }

            const kruzkyData = await kruzkyRes.json();
            const allKruzky = kruzkyData.data || kruzkyData;

            // Update local kruzky state with fresh data
            setKruzky(allKruzky);

            // Find any selected kruzky that are full
            const fullKruzky = selectedKruzky
                .map(id => allKruzky.find((k: any) => k.id === id))
                .filter((k: any) => k && k.capacity !== -1 && (k.currentRegistrations ?? 0) >= k.capacity);

            if (fullKruzky.length > 0) {
                const names = fullKruzky.map((k: any) => k.name).join(', ');
                setErrorAndScroll(`Nasledujúce krúžky sú plné: ${names}. Odstráňte ich z výberu alebo vyberte iné.`);
                return false;
            }

            return true;
        } catch (err) {
            console.error('Failed to verify kruzky capacity:', err);
            setErrorAndScroll('Nepodarilo sa overiť kapacity krúžkov. Skúste to prosím neskôr.');
            return false;
        }
    };

    const handleSubmit = async (targetStatus?: 'DRAFT' | 'GENERATED' | 'ACCEPTED') => {
        // Comprehensive client-side validation
        const requiredFields = ['firstName', 'lastName', 'address', 'houseNumber', 'city', 'psc'];
        if (person?.type !== 'KID') {
            requiredFields.push('email');
        }
        const currentPersonData = {
            firstName: editFormData.firstName || person?.firstName,
            lastName: editFormData.lastName || person?.lastName,
            email: editFormData.email || person?.email,
            address: editFormData.address || person?.address,
            houseNumber: editFormData.houseNumber || person?.houseNumber,
            city: editFormData.city || person?.city,
            psc: editFormData.psc || person?.psc,
            rodCislo: editFormData.rodCislo || person?.rodCislo,
            gender: editFormData.gender || person?.gender,
            schoolyearId: editFormData.schoolyearId || person?.schoolyearId,
            isBernolakova: editFormData.isBernolakova !== undefined ? editFormData.isBernolakova : person?.isBernolakova
        };

        // Check required fields
        for (const field of requiredFields) {
            if (!currentPersonData[field as keyof typeof currentPersonData]?.toString().trim()) {
                setErrorAndScroll(`Prosím vyplňte pole: ${field === 'firstName' ? 'Meno' :
                    field === 'lastName' ? 'Priezvisko' :
                        field === 'email' ? 'Email' :
                            field === 'address' ? 'Ulica' :
                                field === 'houseNumber' ? 'Číslo domu' :
                                    field === 'city' ? 'Mesto' :
                                        field === 'psc' ? 'PSČ' : field}`);
                return;
            }
        }

        // Check email format
        if (currentPersonData.email) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(currentPersonData.email)) {
                setErrorAndScroll('Prosím zadajte správny formát emailovej adresy');
                return;
            }
        }

        // Check gender and schoolyear for non-parent types
        if (person?.type !== 'PARENT') {
            if (!currentPersonData.rodCislo?.trim()) {
                setErrorAndScroll('Prosím vyplňte rodné číslo');
                return;
            }

            // Validate rodCislo format
            let validation;

            // Use child-specific validation for KID type
            if (person?.type === 'KID') {
                validation = validateRodCisloForChild(currentPersonData.rodCislo);
            } else {
                validation = validateRodCislo(currentPersonData.rodCislo);
            }

            if (!validation.isValid) {
                setErrorAndScroll(validation.error || 'Neplatný formát rodného čísla');
                return;
            }

            if (!currentPersonData.gender) {
                setErrorAndScroll('Prosím vyberte pohlavie');
                return;
            }
            if (!currentPersonData.schoolyearId) {
                setErrorAndScroll('Prosím vyberte ročník');
                return;
            }
        }

        // Check kruzky selection
        if (selectedKruzky.length === 0) {
            setErrorAndScroll('Prosím vyberte aspoň jeden krúžok');
            return;
        }

        // Final capacity re-check to avoid race conditions (skip for admin)
        if (!isAdminMode) {
            const capacityOk = await ensureKruzokCapacityBeforeSubmit();
            if (!capacityOk) return;
        }

        // Check payment option
        if (paymentOption === null) {
            setErrorAndScroll('Prosím vyberte spôsob platby');
            return;
        }

        if (!person) return;

        setSubmitting(true);
        setSubmittingStatus(targetStatus || 'GENERATED');
        setError(null);

        // Quick local capacity check to give immediate user feedback without waiting for network
        if (!isAdminMode && selectedKruzky.length > 0) {
            const localFull = selectedKruzky
                .map(id => kruzky.find(k => k.id === id))
                .filter(k => k && k.capacity !== -1 && (k.currentRegistrations ?? 0) >= k.capacity);

            if (localFull.length > 0) {
                const names = localFull.map(k => k!.name).join(', ');
                // Auto-unselect them locally and show message
                suppressClearRef.current = true;
                setSelectedKruzky(prev => prev.filter(id => !localFull.some(k => k!.id === id)));
                setSubmitting(false);
                setErrorAndScroll(`Nasledujúce krúžky sú práve plné a boli automaticky odhlásené: ${names}`);
                return;
            }
        }

        try {
            // First, save person changes if there are any
            let updatedPersonId = person.id;

            // Check if there are changes to save
            const hasPersonChanges = Object.keys(editFormData).some(key => {
                const formValue = editFormData[key as keyof Person];
                const personValue = person[key as keyof Person];
                return formValue !== personValue && formValue !== '';
            });

            if (hasPersonChanges) {
                const endpoint = isAdminMode
                    ? `${process.env.REACT_APP_PATH}/api/admin/person/${person.id}`
                    : `${process.env.REACT_APP_PATH}/api/user/person/${person.id}`;

                const personRes = await fetchWithAuth(endpoint, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(editFormData),
                });

                if (!personRes.ok) {
                    const errorData = await personRes.json();
                    throw new Error(errorData.error || 'Nepodarilo sa aktualizovať údaje osoby');
                }

                const result = await personRes.json();
                // Admin endpoint returns { data: person }, user endpoint returns { person: person }
                updatedPersonId = isAdminMode ? result.data.id : result.person.id;
            }

            // Then create or finalize the prihlaska
            let prihlaška;
            const baseEndpoint = isAdminMode
                ? `${process.env.REACT_APP_PATH}/api/admin/prihlaska`
                : `${process.env.REACT_APP_PATH}/api/prihlaska`;

            if (existingDraftId || existingPrihlaska) {
                const prihlaaskaId = existingDraftId || existingPrihlaska.id;
                const autoApplyVP = shouldAutoApplyVP();
                const effectiveVP = vzdelavacyPoukaz || autoApplyVP;

                if (isAdminMode) {
                    // Admin can directly update any prihlaska
                    prihlaška = await fetchWithAuth(`${baseEndpoint}/${prihlaaskaId}/full`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            personId: updatedPersonId,
                            kruzkyIds: selectedKruzky,
                            vzdelavacyPoukaz: effectiveVP,
                            cestnePrehlasenie: cestnePrehlasenie,
                            paymentOption: paymentOption,
                            toPay: calculateTotalPrice(),
                            status: targetStatus || 'GENERATED' // Default to GENERATED if no status specified
                        }),
                    });
                } else {
                    // User mode - first update the draft with current data
                    const updateRes = await fetch(`${baseEndpoint}/${prihlaaskaId}`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            personId: updatedPersonId,
                            kruzkyIds: selectedKruzky,
                            vzdelavacyPoukaz: effectiveVP,
                            cestnePrehlasenie: cestnePrehlasenie,
                            paymentOption: paymentOption,
                            toPay: calculateTotalPrice()
                        }),
                    });

                    if (!updateRes.ok) {
                        const errorData = await updateRes.json();
                        throw new Error(errorData.error || 'Nepodarilo sa aktualizovať prihlášku');
                    }

                    // Then finalize the draft
                    prihlaška = await fetch(`${baseEndpoint}/${prihlaaskaId}/finalize`, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                    });
                }
            } else {
                // Create new prihlaska
                const autoApplyVP = shouldAutoApplyVP();
                const effectiveVP = vzdelavacyPoukaz || autoApplyVP;

                if (isAdminMode) {
                    // Admin creates directly
                    prihlaška = await fetch(baseEndpoint, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            personId: updatedPersonId,
                            kruzkyIds: selectedKruzky,
                            vzdelavacyPoukaz: effectiveVP,
                            cestnePrehlasenie: cestnePrehlasenie,
                            paymentOption: paymentOption,
                            toPay: calculateTotalPrice(),
                            status: targetStatus || 'GENERATED' // Default to GENERATED if no status specified
                        }),
                    });
                } else {
                    // User creates as DRAFT and finalizes
                    const createRes = await fetchWithAuth(baseEndpoint, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            personId: updatedPersonId,
                            kruzkyIds: selectedKruzky,
                            vzdelavacyPoukaz: effectiveVP,
                            cestnePrehlasenie: cestnePrehlasenie,
                            paymentOption: paymentOption,
                            toPay: calculateTotalPrice()
                        }),
                    });

                    if (!createRes.ok) {
                        const errorData = await createRes.json();
                        throw new Error(errorData.error || 'Nepodarilo sa vytvoriť prihlášku');
                    }

                    const createResult = await createRes.json();
                    const newDraftId = createResult.data.id;

                    // Finalize the newly created draft
                    prihlaška = await fetchWithAuth(`${baseEndpoint}/${newDraftId}/finalize`, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                    });
                }
            }

            if (!prihlaška.ok) {
                const errorData = await prihlaška.json();

                // Handle validation errors from backend
                if (errorData.details && Array.isArray(errorData.details)) {
                    setErrorAndScroll(`Validácia zlyhala: ${errorData.details.join(', ')}`);
                } else {
                    throw new Error(errorData.error || 'Nepodarilo sa dokončiť prihlášku');
                }
                return;
            }

            // Get the finalized prihlaska data
            const finalizedData = await prihlaška.json();
            const prihlaaskaId = finalizedData.data?.id || existingDraftId || existingPrihlaska?.id;

            // Store the prihlaska ID for download
            setGeneratedPrihlaskaId(prihlaaskaId);

            // Call success callback or show popup
            if (onSuccess) {
                onSuccess(prihlaaskaId);
            } else {
                setShowSuccessPopup(true);
            }

        } catch (err) {
            console.error('Failed to save:', err);
            setErrorAndScroll(err instanceof Error ? err.message : 'Nepodarilo sa uložiť údaje');
        }

        setSubmitting(false);
        setSubmittingStatus(null);
    };

    // Download prihlaska as PDF file
    const handleDownloadPrihlaska = async (prihlaaskaId?: string) => {
        const downloadId = prihlaaskaId || generatedPrihlaskaId;
        if (!downloadId) {
            console.error('No prihlaska ID available for download');
            return;
        }

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_PATH}/api/prihlaska/${downloadId}/download`, {
                method: 'GET',
            });

            if (!response.ok) {
                throw new Error('Failed to download prihlaska');
            }

            // Get filename from response headers
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = `${person?.lastName}_${person?.firstName}_prihlaskaSCVC.pdf`;

            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="(.+)"/);
                if (filenameMatch) {
                    filename = filenameMatch[1];
                }
            }

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

        } catch (error) {
            console.error('Error downloading prihlaska:', error);
            alert('Nepodarilo sa stiahnuť prihlášku. Skúste to znova.');
        }
    };

    // Helper function to revoke a generated prihlaska (user mode only)
    const handleRevokePrihlaska = async () => {
        if (isAdminMode || !existingPrihlaska || existingPrihlaska.status !== 'GENERATED') {
            setErrorAndScroll('Možno zrušiť len odoslané prihlášky');
            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_PATH}/api/prihlaska/${existingPrihlaska.id}/revoke`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Nepodarilo sa zrušiť prihlášku');
            }

            // Reload the page to show the prihlaska as draft again
            window.location.reload();

        } catch (err) {
            console.error('Failed to revoke prihlaska:', err);
            setErrorAndScroll(err instanceof Error ? err.message : 'Nepodarilo sa zrušiť prihlášku');
        }

        setSubmitting(false);
    };

    const calculateTotalPrice = () => {
        // Use backend-calculated price if available, otherwise fallback to centralized calculation
        if (totalPrice > 0) {
            return totalPrice;
        }

        if (!person || !settings) return 0;

        // Adapt person object to match priceCalculation utility expectations
        const adaptedPerson = {
            ...person,
            schoolyear: person.schoolyear ? {
                ...person.schoolyear,
                type: person.schoolyear.type === 'PRESCHOOL' || person.schoolyear.type === 'PRIMARY'
                    ? 'OTHER' as const
                    : person.schoolyear.type as 'HIGHSCHOOL' | 'UNIVERSITY'
            } : undefined
        };

        // Automatically apply VP for university students
        const autoApplyVP = shouldAutoApplyVP();
        const effectiveVP = vzdelavacyPoukaz || autoApplyVP;

        return calculatePriceFallback(
            selectedKruzky,
            kruzky,
            adaptedPerson,
            effectiveVP,
            cestnePrehlasenie,
            settings
        );
    };

    const filterCompatibleKruzky = (allKruzky: Kruzok[], person: Person): Kruzok[] => {
        return allKruzky.filter(kruzok => {
            // Filter by gender compatibility
            const genderCompatible = kruzok.gender === 'BOTH' ||
                kruzok.gender === person.gender ||
                !person.gender; // If person has no gender set, show all

            // Filter by schoolyear compatibility
            let schoolyearCompatible = true;
            if (person.schoolyearId && kruzok.schoolYear && kruzok.schoolYear.length > 0) {
                // If both person and kruzok have schoolyear info, check compatibility
                schoolyearCompatible = kruzok.schoolYear.some(sy => sy.id === person.schoolyearId);
            }
            // If either person has no schoolyear or kruzok has no schoolyear restrictions, allow it

            // Filter by Bernolákova school compatibility
            // If person is a KID and doesn't attend Bernolákova school, filter out BERN type kruzky
            const bernolakCompatible = !(!person.isBernolakova && kruzok.type === 'BERN');

            return genderCompatible && schoolyearCompatible && bernolakCompatible;
        });
    };

    // Sort helper: non-stretko kruzky first, then stretko; within groups sort by name (Slovak locale)
    const sortKruzkyByGroup = (list: Kruzok[]) => {
        return list.slice().sort((a, b) => {
            if (a.isStretko === b.isStretko) {
                return a.name.localeCompare(b.name, 'sk');
            }
            return a.isStretko ? 1 : -1;
        });
    };

    // Helper function to determine if person should use SS/Anim pricing
    const shouldUseSsAnimPricing = (): boolean => {
        if (!person) return false;

        // Check if person's school year type is HIGHSCHOOL or UNIVERSITY
        const currentSchoolyearId = editFormData.schoolyearId || person.schoolyearId;
        if (currentSchoolyearId) {
            const schoolyear = schoolyears.find(sy => sy.id === currentSchoolyearId);
            if (schoolyear && (schoolyear.type === 'HIGHSCHOOL' || schoolyear.type === 'UNIVERSITY')) {
                return true;
            }
        }

        // Check if person's rodCislo is in the isanim table
        const currentRodCislo = editFormData.rodCislo || person.rodCislo;
        if (currentRodCislo && isAnimList.some(animator => animator.rodCislo === currentRodCislo)) {
            return true;
        }

        return false;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
                <div className="bg-white shadow-lg rounded-lg p-8 max-w-md w-full">
                    <div className="text-center">
                        <IconLoader className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900">Načítavam údaje...</h2>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Date restriction notice (user mode only) */}
            {!isAdminMode && dateStatus && !dateStatus.canCreate && dateStatus.message && (
                <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-r-md">
                    <div className="flex items-center">
                        <IconX className="h-5 w-5 text-red-400 mr-2" />
                        <p className="text-red-700 font-medium">{dateStatus.message}</p>
                    </div>
                </div>
            )}

            {/* Person Info and Edit Form */}
            <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden">


                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <IconUser className="h-5 w-5 text-blue-600 mr-2" />
                            <h2 className="text-lg font-semibold text-gray-900">
                                Údaje osoby
                            </h2>
                            <span className={`ml-3 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${person.type === 'ADULT' ? 'bg-blue-100 text-blue-800' :
                                person.type === 'PARENT' ? 'bg-purple-100 text-purple-800' :
                                    'bg-green-100 text-green-800'
                                }`}>
                                {person.type === 'ADULT' ? 'Dospelý' : person.type === 'PARENT' ? 'Rodič' : 'Dieťa'}
                            </span>
                        </div>

                        {/* Auto-save indicator */}
                        <div className="flex items-center space-x-2">
                            {autoSaving && (
                                <div className="flex items-center text-blue-600">
                                    <IconLoader className="animate-spin h-4 w-4 mr-1" />
                                    <span className="text-xs">Ukladám...</span>
                                </div>
                            )}
                            {!autoSaving && lastAutoSaveTime && (
                                <div className="flex items-center text-green-600">
                                    <IconCheck className="h-4 w-4 mr-1" />
                                    <span className="text-xs">
                                        Uložené {lastAutoSaveTime.toLocaleTimeString()}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* PersonForm - handles editing and auto-save */}
                <div className="p-6">
                    {isPersonReadOnly && !isAdminMode && (
                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-sm text-yellow-800">
                                <IconInfoCircle className="inline h-4 w-4 mr-1" />
                                Údaje osoby nie je možné editovať po odoslaní prihlášky.
                            </p>
                        </div>
                    )}



                    <PersonForm
                        initialData={{
                            ...person,
                            // Merge any pending edit form data
                            ...editFormData
                        }}
                        onSubmit={async (personData) => {
                            // Handle auto-save functionality
                            if (isPersonReadOnly && !isAdminMode) return;

                            try {
                                const endpoint = isAdminMode
                                    ? `${process.env.REACT_APP_PATH}/api/admin/person/${person.id}`
                                    : `${process.env.REACT_APP_PATH}/api/user/person/${person.id}`;

                                const response = await fetchWithAuth(endpoint, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(personData)
                                });

                                if (!response.ok) {
                                    throw new Error('Failed to save person data');
                                }

                                const result = await response.json();
                                setPerson(result.person || result.data);

                                // Clear edit form data since it's now saved
                                setEditFormData({});
                            } catch (error) {
                                console.error('Auto-save failed:', error);
                                throw error;
                            }
                        }}
                        autosave={!isAdminMode && !isPersonReadOnly}
                        isEmbedded={true}
                        title="Osobné údaje"
                        readOnly={isPersonReadOnly && !isAdminMode}
                    />
                </div>
            </div>

            {/* Show existing prihlaska info if in read-only mode */}
            {hasExistingPrihlaska && existingPrihlaska && isPersonReadOnly && !isAdminMode && (
                <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden mb-6">
                    <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-blue-100">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <IconCheck className="h-5 w-5 text-blue-600 mr-2" />
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Prihlaska pre {person?.firstName} {person?.lastName}
                                </h3>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className={`px-3 py-1 rounded-full text-sm font-medium ${existingPrihlaska.status === 'GENERATED'
                                    ? 'bg-blue-100 text-blue-800'
                                    : existingPrihlaska.status === 'ACCEPTED'
                                        ? 'bg-green-100 text-green-800'
                                        : existingPrihlaska.status === 'REJECTED'
                                            ? 'bg-red-100 text-red-800'
                                            : 'bg-gray-100 text-gray-800'
                                    }`}>
                                    {existingPrihlaska.status === 'GENERATED' && 'Odoslaná'}
                                    {existingPrihlaska.status === 'ACCEPTED' && 'Prijatá'}
                                    {existingPrihlaska.status === 'REJECTED' && 'Zamietnutá'}
                                    {existingPrihlaska.status === 'CANCELLED' && 'Zrušená'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="p-6">
                        {/* Selected Kruzky */}
                        <div className="mb-6">
                            <h4 className="text-md font-medium text-gray-900 mb-3">Vybrané krúžky</h4>
                            {/* BERN surcharge information for kids from Bernolákova */}
                            {person && (editFormData.isBernolakova || person.isBernolakova) && settings?.bernKazdyDalsi && settings?.bernKazdyDalsi > 0 && selectedKruzky.map(id => kruzky.find(k => k.id === id)).filter(k => k && k.type === 'BERN').length > 1 && (
                                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                    <p className="text-sm font-medium text-orange-800 mb-1">
                                        Prvý ZŠ CaM krúžok je za základnú cenu. Každý ďalší ZŠ CaM krúžok má príplatok 80€.
                                    </p>
                                </div>
                            )}
                            <div className="space-y-2">
                                {selectedKruzky.map(kruzokId => {
                                    const kruzok = kruzky.find(k => k.id === kruzokId);
                                    if (!kruzok) return null;

                                    return (
                                        <div key={kruzokId} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                                            <div>
                                                <h5 className="font-medium text-blue-900">{kruzok.name}</h5>
                                                <p className="text-sm text-blue-700">{kruzok.time}</p>
                                                {kruzok.description && (
                                                    <p className="text-xs text-blue-600 mt-1">{kruzok.description}</p>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                <p className="font-medium text-blue-900">
                                                    {kruzok.type === 'BERN' && selectedKruzky.map(id => kruzky.find(k => k.id === id)).filter(k => k && k.type === 'BERN')[0]?.id !== kruzokId
                                                        ?
                                                        <span>{kruzok.price + (settings?.bernKazdyDalsi ?? 0)}€</span>
                                                        :
                                                        <span>{kruzok.price}€</span>
                                                    }
                                                </p>

                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Payment Options */}
                        <div className="mb-6">
                            <h4 className="text-md font-medium text-gray-900 mb-3">Platobné možnosti</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {(vzdelavacyPoukaz || shouldAutoApplyVP()) && (
                                    <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                                        <IconCheck className="h-5 w-5 text-green-600 mr-2" />
                                        <span className="text-green-800">
                                            {shouldAutoApplyVP() ? 'Zľava pre VŠ a Mladých pracujúcich' : 'Vzdelávací poukaz'}
                                        </span>
                                    </div>
                                )}
                                {cestnePrehlasenie && (
                                    <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                                        <IconCheck className="h-5 w-5 text-green-600 mr-2" />
                                        <span className="text-green-800">Čestné prehlásenie</span>
                                    </div>
                                )}
                                {paymentOption && (
                                    <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <IconCreditCard className="h-5 w-5 text-blue-600 mr-2" />
                                        <span className="text-blue-800">
                                            {paymentOption === 'CASH' ? 'Hotovosť' : 'EduPage'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Price Summary / Breakdown (show detailed breakdown when read-only) */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            {priceBreakdown ? (
                                <>

                                    <div className="space-y-2">
                                        {priceBreakdown.breakdown.filter((item: any) => item.type === 'kruzok').map((item: any) => (
                                            <div key={item.name || item.label} className="flex justify-between items-center py-1 border-b border-gray-100">
                                                <span className="text-sm text-gray-700">{item.name}</span>
                                                <span className="text-sm text-gray-700">{item.totalPrice}€</span>
                                            </div>
                                        ))}
                                        {Array.isArray(priceBreakdown.breakdown) && priceBreakdown.breakdown.filter((item: any) => item.type !== 'kruzok').map((item: any) => (
                                            <div key={item.name || item.label} className="flex justify-between items-center py-1 border-b border-gray-100">
                                                <span className="text-sm text-gray-700">{item.label || item.name || item.description || ''}</span>
                                                <span className="text-sm text-gray-700">{item.type === 'admin_reduction' ? '-' : ''}{(item.amount ?? item.value ?? item.total ?? item.baseFee ?? item.price ?? 0)}€</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex justify-between items-center py-2 font-semibold text-lg border-t border-gray-300 mt-3">
                                        <span>Celková cena:</span>
                                        <span className="text-blue-600">{priceBreakdown.total ?? calculateTotalPrice()}€</span>
                                    </div>

                                    <span className="text-sm text-gray-500">
                                        Keďže nechceme, aby financie boli dôvodom nenavštevovania stretka, alebo krúžku Vaším dieťaťom, je tu
                                        možnosť úhrady na splátky, resp. znížená suma po osobnej dohode v kancelárii SCVČ.
                                    </span>
                                </>
                            ) : (
                                <>
                                    <div className="flex justify-between items-center py-2 font-semibold text-lg">
                                        <span>Celková cena:</span>
                                        <span className="text-blue-600">{calculateTotalPrice()}€</span>
                                    </div>
                                    <span className="text-sm text-gray-500">
                                        Keďže nechceme, aby financie boli dôvodom nenavštevovania stretka, alebo krúžku Vaším dieťaťom, je tu
                                        možnosť úhrady na splátky, resp. znížená suma po osobnej dohode v kancelárii SCVČ.
                                    </span>
                                </>

                            )}
                        </div>

                        {/* Actions for GENERATED status */}
                        {existingPrihlaska.status === 'GENERATED' && (
                            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                                <div className="flex items-start">
                                    <IconInfoCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                                    <div className="flex-1">
                                        <h5 className="font-medium text-yellow-800 mb-2">Prihláška bola odoslaná</h5>
                                        <p className="text-sm text-yellow-700 mb-3">
                                            Prihláška je momentálne v stave "Odoslaná". Pre jej schválenie je nutné ju vytlačiť a podpísanú spolu s rozhodnutím o prijatí odovzdať v kancelárii SCVČ Tri Hôrky.
                                            Ak potrebujete urobiť zmeny, môžete odoslanie zrušiť.
                                        </p>
                                        <div className="flex gap-2 flex-wrap">
                                            <button
                                                onClick={() => handleDownloadPrihlaska(existingPrihlaska.id)}
                                                className="inline-flex items-center px-4 py-2 border border-green-600 text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200"
                                            >
                                                <IconDownload className="h-4 w-4 mr-2" />
                                                Stiahnuť prihlášku
                                            </button>
                                            <button
                                                onClick={handleRevokePrihlaska}
                                                disabled={submitting || Boolean(!isAdminMode && dateStatus &&
                                                    (!dateStatus.canEdit || (dateStatus.finalDate && new Date() > new Date(dateStatus.finalDate))))}
                                                className="inline-flex items-center px-4 py-2 border border-yellow-600 text-sm font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {submitting ? (
                                                    <IconLoader className="animate-spin h-4 w-4 mr-2" />
                                                ) : (
                                                    <IconX className="h-4 w-4 mr-2" />
                                                )}
                                                {submitting ? 'Ruším prihlášku...' : 'Zrušiť odoslanie'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Info for other statuses */}
                        {existingPrihlaska.status !== 'GENERATED' && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                                <div className="flex items-start">
                                    <IconInfoCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                                    <div>
                                        <h5 className="font-medium text-blue-800 mb-2">Stav prihlášky</h5>
                                        <p className="text-sm text-blue-700">
                                            {existingPrihlaska.status === 'ACCEPTED' && 'Prihlaska bola prijatá a spracovaná.'}
                                            {existingPrihlaska.status === 'REJECTED' && 'Prihlaska bola zamietnutá.'}
                                            {existingPrihlaska.status === 'CANCELLED' && 'Prihlaska bola zrušená.'}
                                            {!['ACCEPTED', 'REJECTED', 'CANCELLED'].includes(existingPrihlaska.status) && 'Prihlaska je v spracovaní.'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Show form if not in read-only mode or if admin */}
            {(!hasExistingPrihlaska || !isPersonReadOnly || isAdminMode) && (
                <>
                    {/* Available Kruzky */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden mb-6">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <IconSchool className="h-5 w-5 text-blue-600 mr-2" />
                                    <h3 className="text-lg font-semibold text-gray-900">Dostupné krúžky</h3>
                                </div>
                                <div className="text-sm text-gray-600">
                                    {isAdminMode
                                        ? `${filteredKruzky.length} krúžkov (všetky dostupné pre admin)`
                                        : `${filteredKruzky.length} z ${kruzky.length} krúžkov kompatibilných`
                                    }
                                </div>
                            </div>
                            {person && !isAdminMode && (
                                <div className="mt-2 text-xs text-gray-500">
                                    Filtrované podľa:
                                    <span className="ml-1 inline-flex items-center px-2 py-1 rounded-full bg-gray-100">
                                        Pohlavie:
                                        {editFormData.gender && (editFormData.gender === 'MALE' ? ' Chlapci' : ' Dievčatá')}
                                        {!editFormData.gender && !person.gender && ' Nezadané'}
                                    </span>
                                    {(editFormData.schoolyearId || person.schoolyearId) && (
                                        <span className="ml-1 inline-flex items-center px-2 py-1 rounded-full bg-gray-100">
                                            Ročník: {schoolyears.find(sy => sy.id === (editFormData.schoolyearId || person.schoolyearId))?.name || 'Nezadaný'}
                                        </span>
                                    )}
                                </div>
                            )}
                            {isAdminMode && (
                                <div className="mt-2 text-xs text-gray-500">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-amber-100 text-amber-800">
                                        Admin režim: Všetky krúžky dostupné bez obmedzení
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="p-6">

                            {isAdminMode && filteredKruzky.length > 0 && (
                                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-sm font-medium text-blue-800 mb-2">Farebné označenie krúžkov (admin režim):</p>
                                    <div className="flex flex-wrap gap-4 text-xs">
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 border border-green-300 bg-green-50 rounded"></div>
                                            <span className="text-green-700">Viditeľné pre užívateľov</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 border border-gray-300 bg-gray-50 rounded"></div>
                                            <span className="text-gray-600">Len pre adminov (filtrovanie/obmedzenia)</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 border border-blue-500 bg-blue-50 rounded"></div>
                                            <span className="text-blue-700">Vybraté</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* BERN surcharge information for kids from Bernolákova */}
                            {person && (editFormData.isBernolakova || person.isBernolakova) && settings?.bernKazdyDalsi && settings?.bernKazdyDalsi > 0 && (
                                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                    <p className="text-sm font-medium text-orange-800 mb-1">
                                        Prvý ZŠ CaM krúžok je za základnú cenu. Každý ďalší ZŠ CaM krúžok má príplatok 80€.
                                    </p>
                                </div>
                            )}

                            {filteredKruzky.length === 0 ? (
                                <div className="text-center py-8">
                                    <IconSchool className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                                    <p className="text-gray-500">
                                        {kruzky.length === 0
                                            ? "Momentálne nie sú dostupné žiadne krúžky"
                                            : "Žiadne krúžky nie sú kompatibilné s touto osobou (pohlavie/ročník)"}
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {sortKruzkyByGroup(filteredKruzky).map((kruzok) => {
                                        const isFull = isKruzokFull(kruzok);
                                        const isSelected = selectedKruzky.includes(kruzok.id);
                                        const isDisabled = (!isAdminMode && dateStatus && !dateStatus.canEdit) || (!isAdminMode && isFull && !isSelected);
                                        const isUserCompatible = isAdminMode && userCompatibleKruzky.includes(kruzok.id);

                                        let borderClass = '';
                                        let bgClass = '';

                                        if (isDisabled) {
                                            borderClass = 'border-gray-200 opacity-50 cursor-not-allowed';
                                            bgClass = 'bg-gray-50';
                                        } else if (isSelected) {
                                            borderClass = 'border-blue-500 cursor-pointer';
                                            bgClass = 'bg-blue-50';
                                        } else if (isUserCompatible) {
                                            // Highlight user-compatible kruzky in admin mode
                                            borderClass = 'border-green-300 hover:border-green-400 cursor-pointer';
                                            bgClass = 'bg-green-50 hover:bg-green-100';
                                        } else {
                                            borderClass = isAdminMode ? 'border-gray-300 hover:border-gray-400 cursor-pointer' : 'border-gray-200 hover:border-gray-300 cursor-pointer';
                                            bgClass = isAdminMode ? 'bg-gray-50 hover:bg-gray-100' : '';
                                        }

                                        return (
                                            <div
                                                key={kruzok.id}
                                                className={`border rounded-lg p-4 transition-all ${borderClass} ${bgClass}`}
                                                onClick={() => !isDisabled && handleKruzokToggle(kruzok.id)}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1">
                                                        <h4 className={`font-medium mb-1 ${isDisabled ? 'text-gray-500' : 'text-gray-900'}`}>
                                                            {kruzok.name}
                                                            {isFull && !isSelected && !isAdminMode && (
                                                                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                    Plné
                                                                </span>
                                                            )}
                                                            {isFull && isAdminMode && (
                                                                <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                                                    Plné (admin: povolené)
                                                                </span>
                                                            )}
                                                        </h4>
                                                        {kruzok.description && (
                                                            <p className={`text-sm mb-2 ${isDisabled ? 'text-gray-400' : 'text-gray-600'}`}>
                                                                {kruzok.description}
                                                            </p>
                                                        )}
                                                        <div className={`text-sm space-y-1 ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                                                            <div>Čas: {kruzok.time}</div>
                                                            {kruzok.price > 0 && <div>Príplatok: {kruzok.price}€ {(kruzok.type === 'BERN' && (settings?.bernKazdyDalsi ?? 0) > 0) && `/ ${kruzok.price + (settings?.bernKazdyDalsi ?? 0)}€`}</div>}
                                                            {kruzok.capacity > 0 && (
                                                                <div className="flex items-center space-x-2">
                                                                    <span>Prihlásených: {kruzok.currentRegistrations}/{kruzok.capacity}</span>
                                                                    {isFull && (
                                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                                            Plné
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {kruzok.capacity === -1 && (
                                                                <div>Prihlásených: {kruzok.currentRegistrations}</div>
                                                            )}
                                                            <div className="flex space-x-2">
                                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${kruzok.type === 'TRIH' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                                                                    }`}>
                                                                    {kruzok.type === 'TRIH' ? '3hôrky' : 'Bernolákova'}
                                                                </span>
                                                                {kruzok.isStretko && (
                                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                        Stretko
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {isSelected && (
                                                        <div className="ml-3">
                                                            <IconCheck className="h-5 w-5 text-blue-600" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Application Type and Payment Options */}
                    <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden mb-6">
                        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100">
                            <div className="flex items-center">
                                <IconCalendar className="h-5 w-5 text-purple-600 mr-2" />
                                <h3 className="text-lg font-semibold text-gray-900">Dodatočné možnosti a platba</h3>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="space-y-6">
                                {/* Vzdelávací poukaz and Čestné prehlásenie */}
                                {discountsAllowed && <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Dodatočné možnosti
                                    </label>
                                    <div className="space-y-3">
                                        {/* Show VP option only for non-university students */}
                                        {!shouldAutoApplyVP() && (
                                            <div
                                                onClick={() => !(!isAdminMode && dateStatus && !dateStatus.canEdit) && setVzdelavacyPoukaz(!vzdelavacyPoukaz)}
                                                className={`border-2 rounded-lg p-4 transition-all ${(!isAdminMode && dateStatus && !dateStatus.canEdit)
                                                    ? 'border-gray-200 opacity-50 cursor-not-allowed'
                                                    : vzdelavacyPoukaz
                                                        ? 'border-purple-500 bg-purple-50 cursor-pointer'
                                                        : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                                                    }`}
                                            >
                                                <div className="flex items-center">
                                                    <div className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center ${vzdelavacyPoukaz
                                                        ? 'border-purple-500 bg-purple-500'
                                                        : 'border-gray-300'
                                                        }`}>
                                                        {vzdelavacyPoukaz && (
                                                            <IconCheck className="h-3 w-3 text-white" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-medium text-gray-900">Vzdelávací poukaz</h4>
                                                        <p className="text-sm text-gray-600">Vzdelávací poukaz vydáva škola. Odovzdaním VP nám
                                                            sprostredkujete možnosť čerpania príspevku zo štátneho rozpočtu. Odovzdaním ušetríte {person.schoolyear?.type === 'HIGHSCHOOL' || person.schoolyear?.type === 'UNIVERSITY' ? settings?.VPvalueSsAnim : settings?.VPvalue}€</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div
                                            onClick={() => !(!isAdminMode && dateStatus && !dateStatus.canEdit) && setCestnePrehlasenie(!cestnePrehlasenie)}
                                            className={`border-2 rounded-lg p-4 transition-all ${(!isAdminMode && dateStatus && !dateStatus.canEdit)
                                                ? 'border-gray-200 opacity-50 cursor-not-allowed'
                                                : cestnePrehlasenie
                                                    ? 'border-purple-500 bg-purple-50 cursor-pointer'
                                                    : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                                                }`}
                                        >
                                            <div className="flex items-center">
                                                <div className="w-9 ">
                                                    <div className={`w-5 h-5 border-2 rounded mr-3 flex items-center justify-center ${cestnePrehlasenie
                                                        ? 'border-purple-500 bg-purple-500'
                                                        : 'border-gray-300'
                                                        }`}>
                                                        {cestnePrehlasenie && (
                                                            <IconCheck className="h-3 w-3 text-white" />
                                                        )}
                                                    </div>
                                                </div>

                                                <div>
                                                    <h4 className="font-medium text-gray-900">Čestné prehlásenie</h4>
                                                    <p className="text-sm text-gray-600">Čestné prehlásenie môžete odovzdať len do jedného CVČ. Odovzdaním ČP nám sprostredkujete možnosť čerpania príspevku z podielových daní mesta. Odovzdaním ušetríte {person.schoolyear?.type === 'HIGHSCHOOL' || person.schoolyear?.type === 'UNIVERSITY' ? settings?.CPvalueSsAnim : settings?.CPvalue}€</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>}

                                {/* Payment Option Selection */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-3 ">
                                        Spôsob platby *
                                    </label>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div
                                            onClick={() => !(!isAdminMode && dateStatus && !dateStatus.canEdit) && setPaymentOption('CASH')}
                                            className={`border-2 rounded-lg p-4 transition-all ${(!isAdminMode && dateStatus && !dateStatus.canEdit)
                                                ? 'border-gray-200 opacity-50 cursor-not-allowed'
                                                : paymentOption === 'CASH'
                                                    ? 'border-blue-500 bg-blue-50 cursor-pointer'
                                                    : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                                                }`}
                                        >
                                            <div className="flex items-center">
                                                <div className={`w-4 h-4 rounded-full border-2 mr-3 ${paymentOption === 'CASH'
                                                    ? 'border-blue-500 bg-blue-500'
                                                    : 'border-gray-300'
                                                    }`}>
                                                    {paymentOption === 'CASH' && (
                                                        <div className="w-full h-full rounded-full bg-white transform scale-50"></div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="flex items-center">
                                                        <IconCreditCard className="h-4 w-4 text-blue-600 mr-1" />
                                                        <h4 className="font-medium text-gray-900">Hotovosť</h4>
                                                    </div>
                                                    <p className="text-sm text-gray-600">Platba v hotovosti</p>
                                                </div>
                                            </div>
                                        </div>

                                        <div
                                            onClick={() => !(!isAdminMode && dateStatus && !dateStatus.canEdit) && setPaymentOption('EDUPAGE')}
                                            className={`border-2 rounded-lg p-4 transition-all ${(!isAdminMode && dateStatus && !dateStatus.canEdit)
                                                ? 'border-gray-200 opacity-50 cursor-not-allowed'
                                                : paymentOption === 'EDUPAGE'
                                                    ? 'border-blue-500 bg-blue-50 cursor-pointer'
                                                    : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                                                }`}
                                        >
                                            <div className="flex items-center">
                                                <div className={`w-4 h-4 rounded-full border-2 mr-3 ${paymentOption === 'EDUPAGE'
                                                    ? 'border-blue-500 bg-blue-500'
                                                    : 'border-gray-300'
                                                    }`}>
                                                    {paymentOption === 'EDUPAGE' && (
                                                        <div className="w-full h-full rounded-full bg-white transform scale-50"></div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="flex items-center">
                                                        <IconCalendar className="h-4 w-4 text-blue-600 mr-1" />
                                                        <h4 className="font-medium text-gray-900">Edupage</h4>
                                                    </div>
                                                    <p className="text-sm text-gray-600">Platba prevodom alebo kartou (cez Edupage)</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Summary */}
                    {selectedKruzky.length > 0 && (
                        <div className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden mb-6">
                            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-green-100">
                                <h3 className="text-lg font-semibold text-gray-900">Súhrn prihlášky{shouldUseSsAnimPricing() ? ' - Animátor / Stredoškolák a starší' : ''}</h3>
                            </div>
                            <div className="p-6">
                                <div className="space-y-2">
                                    {/* Price breakdown from backend */}
                                    {priceBreakdown ? (
                                        // Use backend price breakdown for summary
                                        <>
                                            {priceBreakdown.breakdown.filter((item: any) => item.type === 'kruzok').map((item: any) => (
                                                <div key={item.name} className="py-2 border-b border-gray-100">
                                                    <div className="flex justify-between items-center">
                                                        <span className="font-medium">{item.name}</span>
                                                        <span className="text-gray-600">{item.totalPrice}€</span>
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Registration fee */}
                                            {priceBreakdown.breakdown.filter((item: any) => item.type === 'zapisne').map((item: any) => (
                                                <div key={item.name} className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="font-medium">{item.name}</span>
                                                    <span className="text-gray-600">{item.amount}€</span>
                                                </div>
                                            ))}

                                            {/* VP discount */}
                                            {priceBreakdown.breakdown.filter((item: any) => item.type === 'vp_discount').map((item: any) => (
                                                <div key={item.name} className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="font-medium text-green-600">{item.name}</span>
                                                    <span className="text-green-600">{item.amount}€</span>
                                                </div>
                                            ))}

                                            {/* CP discount */}
                                            {priceBreakdown.breakdown.filter((item: any) => item.type === 'cp_discount').map((item: any) => (
                                                <div key={item.name} className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="font-medium text-green-600">{item.name}</span>
                                                    <span className="text-green-600">{item.amount}€</span>
                                                </div>
                                            ))}

                                            {/* Admin reduction */}
                                            {priceBreakdown.breakdown.filter((item: any) => item.type === 'admin_reduction').map((item: any) => (
                                                <div key={item.name} className="flex justify-between items-center py-2 border-b border-gray-100">
                                                    <span className="font-medium text-blue-600">{item.name}</span>
                                                    <span className="text-blue-600">-{item.amount}€</span>
                                                </div>
                                            ))}

                                            {/* Total from backend */}
                                            <div className="flex justify-between items-center py-2 font-semibold text-lg border-t border-gray-300 mt-2 pt-3">
                                                <div className="flex flex-col ">
                                                    <span>Celková cena na školský rok:</span>
                                                </div>


                                                <span className="text-blue-600">{priceBreakdown.totalPrice}€</span>
                                            </div>


                                        </>
                                    ) : (
                                        // Fallback to old calculation when no breakdown available
                                        <>
                                            {sortKruzkyByGroup(kruzky.filter(k => selectedKruzky.includes(k.id))).map(kruzok => {
                                                if (!kruzok) return null;

                                                const useSsAnimPricing = shouldUseSsAnimPricing();
                                                const baseFee = useSsAnimPricing ?
                                                    (kruzok.isStretko && settings?.baseStretkoSsAnim !== undefined ? settings.baseStretkoSsAnim :
                                                        (!kruzok.isStretko && settings?.baseKruzokSsAnim !== undefined ? settings.baseKruzokSsAnim : 0)) :
                                                    (kruzok.isStretko && settings?.baseStretko ? settings.baseStretko :
                                                        (!kruzok.isStretko && settings?.baseKruzok ? settings.baseKruzok : 0));
                                                const totalKruzokPrice = kruzok.price + baseFee;

                                                return (
                                                    <div key={kruzok.id} className="py-2 border-b border-gray-100">
                                                        <div className="flex justify-between items-center">
                                                            <span className="font-medium">{kruzok.name}</span>
                                                            <span className="text-gray-600">{totalKruzokPrice}€</span>
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            {/* Registration fee */}
                                            {(() => {
                                                const useSsAnimPricing = shouldUseSsAnimPricing();
                                                const zapisneValue = useSsAnimPricing && settings?.zapisneValueSsAnim !== undefined ?
                                                    settings.zapisneValueSsAnim : settings?.zapisneValue;
                                                const hasRequiredKruzky = selectedKruzky.some(kruzokId => {
                                                    const kruzok = kruzky.find(k => k.id === kruzokId);
                                                    return kruzok?.requiresZapisne === true;
                                                });

                                                return zapisneValue && zapisneValue > 0 && hasRequiredKruzky && (
                                                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                        <span className="font-medium">Zápisné - prevádzka strediska</span>
                                                        <span className="text-gray-600">{zapisneValue}€</span>
                                                    </div>
                                                );
                                            })()}

                                            {/* VP discount */}
                                            {(() => {
                                                const useSsAnimPricing = shouldUseSsAnimPricing();
                                                const vpValue = useSsAnimPricing && settings?.VPvalueSsAnim !== undefined ?
                                                    settings.VPvalueSsAnim : settings?.VPvalue;
                                                const autoApplyVP = shouldAutoApplyVP();


                                                const showVP = (vzdelavacyPoukaz || autoApplyVP) && vpValue && vpValue > 0;

                                                return showVP && (
                                                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                        <span className="font-medium text-green-600">
                                                            {autoApplyVP ? 'Zľava pre VŠ a Mladých pracujúcich' : 'Vzdelávací poukaz (zľava)'}
                                                        </span>
                                                        <span className="text-green-600">-{vpValue}€</span>
                                                    </div>
                                                );
                                            })()}

                                            {/* CP discount */}
                                            {(() => {
                                                const useSsAnimPricing = shouldUseSsAnimPricing();
                                                const cpValue = useSsAnimPricing && settings?.CPvalueSsAnim !== undefined ?
                                                    settings.CPvalueSsAnim : settings?.CPvalue;

                                                return cestnePrehlasenie && cpValue && cpValue > 0 && (
                                                    <div className="flex justify-between items-center py-2 border-b border-gray-100">
                                                        <span className="font-medium text-green-600">Čestné prehlásenie (zľava)</span>
                                                        <span className="text-green-600">-{cpValue}€</span>
                                                    </div>
                                                );
                                            })()}

                                            <div className="flex justify-between items-center py-2 font-semibold text-lg border-t border-gray-300 mt-2 pt-3">
                                                <span>Celková cena:</span>
                                                <span className="text-blue-600">{calculateTotalPrice()}€</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-between items-center pt-6">
                        {onCancel && (
                            <button
                                onClick={onCancel}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                            >
                                Zrušiť
                            </button>
                        )}

                        <div className="flex space-x-3 ml-auto">
                            {!isAdminMode && (
                                <button
                                    onClick={handleSaveDraft}
                                    disabled={Boolean(savingDraft || (!isAdminMode && dateStatus && !dateStatus.canEdit))}
                                    className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center space-x-2"
                                >
                                    {savingDraft ? (
                                        <IconLoader className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <IconDeviceFloppy className="h-4 w-4" />
                                    )}
                                    <span>{savingDraft ? 'Ukladám...' : 'Uložiť návrh'}</span>
                                </button>
                            )}

                            {isAdminMode ? (
                                // Admin mode: Show three save buttons
                                <>
                                    <button
                                        onClick={() => handleSubmit('DRAFT')}
                                        disabled={Boolean(submitting)}
                                        className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center space-x-2"
                                    >
                                        {submitting && submittingStatus === 'DRAFT' ? (
                                            <IconLoader className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <IconDeviceFloppy className="h-4 w-4" />
                                        )}
                                        <span>{(submitting && submittingStatus === 'DRAFT') ? 'Ukladám...' : 'Uložiť ako koncept'}</span>
                                    </button>

                                    <button
                                        onClick={() => handleSubmit('GENERATED')}
                                        disabled={Boolean(submitting)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center space-x-2"
                                    >
                                        {submitting && submittingStatus === 'GENERATED' ? (
                                            <IconLoader className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <IconSend className="h-4 w-4" />
                                        )}
                                        <span>{(submitting && submittingStatus === 'GENERATED') ? 'Ukladám...' : 'Uložiť ako vygenerovanú'}</span>
                                    </button>

                                    <button
                                        onClick={() => handleSubmit('ACCEPTED')}
                                        disabled={Boolean(submitting)}
                                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center space-x-2"
                                    >
                                        {submitting && submittingStatus === 'ACCEPTED' ? (
                                            <IconLoader className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <IconCheck className="h-4 w-4" />
                                        )}
                                        <span>{(submitting && submittingStatus === 'ACCEPTED') ? 'Ukladám...' : 'Uložiť ako prijatú'}</span>
                                    </button>
                                </>
                            ) : (
                                // User mode: Show single submit button
                                <button
                                    onClick={() => handleSubmit()}
                                    disabled={Boolean(submitting || (!isAdminMode && dateStatus && !dateStatus.canSubmit))}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center space-x-2"
                                >
                                    {submitting ? (
                                        <IconLoader className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <IconSend className="h-4 w-4" />
                                    )}
                                    <span>{submitting ? 'Ukladám...' : 'Odoslať prihlášku'}</span>
                                </button>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Success Popup */}
            {showSuccessPopup && (
                <>
                    {isAdminMode ? (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                                <div className="text-center">
                                    <IconCheck className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Prihlaska bola úspešne odoslaná!
                                    </h3>
                                    <p className="text-gray-600 mb-6">
                                        Vaša prihlaska bola spracovaná a odoslaná na schválenie.
                                    </p>
                                    <div className="space-y-3">
                                        <button
                                            onClick={() => handleDownloadPrihlaska()}
                                            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium flex items-center justify-center space-x-2"
                                        >
                                            <IconDownload className="h-4 w-4" />
                                            <span>Stiahnuť prihlášku</span>
                                        </button>
                                        <button
                                            onClick={() => setShowSuccessPopup(false)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 font-medium"
                                        >
                                            Zatvoriť
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-lg p-8 max-w-xl w-full mx-4">
                                <div className="text-center">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <IconCheck className="w-8 h-8 text-green-600" />
                                    </div>

                                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                                        Prihlaska úspešne odoslaná!
                                    </h3>

                                    <div className="text-sm text-gray-600 mb-6 space-y-2">
                                        <div className="flex items-start">
                                            <IconInfoCircle className="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" />
                                            <div className="text-left">
                                                <p className="font-medium">Ďalšie kroky:</p>
                                                <ul className="mt-1 space-y-1 lg:*:text-nowrap ">
                                                    <li>• Údaje už nie je možné meniť</li>
                                                    <li>• Stiahnite, vytlačte a podpíšte prihlášku</li>
                                                    <li>• Prihlášku a rozhodnutie doneste do kancelárie SCVČ</li>
                                                    <li>• Prihláška je platná až po jej odovzdaní</li>
                                                    <li>• Stav prihlášky môžete sledovať vo svojom profile</li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <button
                                            onClick={() => handleDownloadPrihlaska()}
                                            className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center"
                                        >
                                            <IconDownload size={20} className="mr-2" />
                                            Stiahnuť prihlášku
                                        </button>

                                        <button
                                            onClick={() => {
                                                setShowSuccessPopup(false);
                                                navigate('/main');
                                            }}
                                            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
                                        >
                                            Pokračovať
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </>

            )}

            {/* Error Message */}
            {error && (
                <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-r-md">
                    <div className="flex items-center">
                        <IconX className="h-5 w-5 text-red-400 mr-2" />
                        <p className="text-red-700">{error}</p>
                    </div>
                </div>
            )}

            {/* Success Message */}
            {draftSaved && (
                <div className="p-4 bg-green-50 border-l-4 border-green-400 rounded-r-md">
                    <div className="flex items-center">
                        <IconCheck className="h-5 w-5 text-green-400 mr-2" />
                        <p className="text-green-700">Návrh bol úspešne uložený!</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SharedPrihlaskaForm;
